import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { getDateFromOffset } from '../../../helpers/dates';
import { WelletContext } from '../../../context/WelletContext';
import { useTranslation } from 'react-i18next';
import ContentApp from '../../../components/ContentApp';
import Balance from '../../../components/Balance/Balance';
import AccountHeader from '../../../components/AccountHeader/AccountHeader';
import WarningBox from '../../../components/Common/WarningBox/WarningBox';
import WithdrawalMethodWarningBox from '../../../components/Common/WithdrawalMethodWarningBox/WithdrawalMethodWarningBox';
import CustomButton from '../../../components/ButtomCustom/CustomButton';
import ArrowIcon from '../../../components/icons/ArrowIcon';
import NavSelector from '../../../components/NavSelector/NavSelector';
import LiveFeedActivities from '../../../components/LiveFeedActivities/LiveFeedActivities';
import AmbassadorNetworkIcon from '../../../components/icons/AmbassadorNetworkIcon';
import ReferralIcon from '../../../components/icons/ReferralIcon';
import ReservationsList from '../../../components/ReservationsList/ReservationsList';

const Account = ({ history }) => {
  const { t } = useTranslation();
  const welletContext = useContext(WelletContext);
  const jwt = useSelector((state) => state.associates.jwt);
  const user = useSelector((state) => state.associates.user);
  const today = getDateFromOffset(user.companyUtcMinutesOffset);
  const todayFilter = format(today, 'yyyy-MM-dd');
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [myEarningsToday, setMyEarningsToday] = useState(null);
  const [monthEarning, setMonthEarning] = useState(0);
  const kycStatus = useSelector((state) => state.associates.user.kycStatus);
  const advantageNetworkStatus = useSelector(
    (state) => state?.associates?.user?.advantageNetworkStatus,
  );
  const app = useSelector((state) => state.app);

  const getMyEarningsToday = async () => {
    setLoadingBalance(true);
    try {
      const balance = await welletContext.apis.tickets.get(
        `/associates/GetEarnings?from=${todayFilter}&to=${todayFilter}`,
      );
      setMyEarningsToday(balance.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const getEarnings = async (fromDate, toDate) => {
    setLoadingBalance(true);
    try {
      const balance = await welletContext.apis.tickets.get(
        `/associates/GetEarnings?from=${fromDate}&to=${toDate}`,
      );
      setMonthEarning(balance.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const handleUpdateEarnings = () => {
    getMyEarningsToday();

    const date = getDateFromOffset(user.companyUtcMinutesOffset);
    const firstMonthDate = format(startOfMonth(date), 'yyyy-MM-dd');
    const lastMonthDate = format(endOfMonth(date), 'yyyy-MM-dd');

    getEarnings(firstMonthDate, lastMonthDate);
  }

  useEffect(() => {
    handleUpdateEarnings();
  }, [jwt]);

  return (
    <>
      <AccountHeader title={app.name} icon={'config'} pathName='/configuration' />
      <ContentApp menu header>
        <div className='mb-2'>
          {advantageNetworkStatus !== 'APPROVED' ? (
            <WarningBox
              title={t('advantageNetworkStatus.title')}
              subtitle={t('advantageNetworkStatus.subtitle')}
            />
          ) : (
            kycStatus === 'REJECTED' && (
              <WarningBox
                title={t('identityNotValidated.title')}
                subtitle={t('identityNotValidated.subtitle')}
                textLink={t('identityNotValidated.textLink')}
                onClickText={() => history.push('/kyc-start')}
              />
            )
          )}
        </div>
        <WithdrawalMethodWarningBox />
        <div className='mt-2'>
          <Balance
            title={t('earnings')}
            todayBalance={myEarningsToday?.amount}
            last7DaysBalance={monthEarning?.amount}
            showText={true}
            fontSize='1.8rem'
            showAllBalances={true}
            loading={loadingBalance}
            mpVerified={true}
            showArrow={true}
          />
        </div>

        <div className='d-flex mt-3'>
          <div className='d-flex flex-column' style={{ width: '50%' }}>
            <CustomButton
              rightIcon={<ArrowIcon size={15} />}
              leftIcon={<AmbassadorNetworkIcon size={24} color='#000' />}
              text={t('inviteAndWin')}
              description={t('inviteAndWinDescription')}
              onClick={() => history.push('/my-network')}
            />
          </div>
          <div className='w-50 ml-2'>
            <CustomButton
              rightIcon={<ArrowIcon size={15} />}
              leftIcon={<ReferralIcon size={20} color='#000' />}
              text={t('createReservation')}
              description={t('createReservationDescription')}
              onClick={() => history.push('/restaurant-list')}
            />
          </div>
        </div>

        <ReservationsList onRealTimeUpdate={handleUpdateEarnings} />
        <LiveFeedActivities />
        <NavSelector history={history} />
      </ContentApp>
    </>
  );
};

export default Account;
